import axios, {
  AxiosError,
  type AxiosAdapter,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { toast } from '@/stores/toast.store';
import { revokeRefreshToken, useAuthStore } from '@/stores/auth.store';
import { clearImpersonation, getImpersonation, getStoredImpersonation } from '@/lib/impersonation';
import { publicApi } from '@/lib/public-api';
import { recordSignOut } from '@/lib/signOutReason';

export interface ApiError {
  message: string;
  statusCode: number;
  errors?: Record<string, string[]>;
}

// ─── Retry config ──────────────────────────────────────────────────────
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;
// No 429: the API blocks a rate-limit bucket for up to a minute, so a retry one or two seconds later is
// always refused again. It only adds load; a poll tries again on its next tick anyway.
const RETRYABLE_STATUS_CODES = new Set([408, 500, 502, 503, 504]);

interface RetryConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
  /** GETs only. Identical GETs in flight together are sent once (see shareInflightGets). */
  _dedupeKey?: string;
  /** The token this client put on the request (null: none was stored). Unset when the caller named its own. */
  _sentToken?: string | null;
  /** Sent with this tab's Login-as-Partner token. */
  _asPartner?: boolean;
  /** Already sent again once because it carried a token that had since been replaced. */
  _staleTokenRetried?: boolean;
  /** Already sent again once after renewing the session. */
  _sessionRenewed?: boolean;
  /** The page (pathname) that sent the request. A failed request is not retried once the user has left it. */
  _page?: string;
}

// ─── GET request deduplication ──────────────────────────────────────────
// Prevents duplicate simultaneous GET requests (e.g. component remounts): identical GETs in flight at
// the same time go out once, and every caller gets the answer.
//
// It is done in the adapter, under the interceptors, because every request passes through there.
// axios.create() binds get/post/… to its internal context, so they never reach an override of
// api.request: the wrapper that used to do this there only caught direct api.request calls, and the app
// makes none.
//
// Every caller still runs its own interceptors on its own copy of the outcome, so a 401 renews and
// retries per caller as before. A retry comes back through here and is never handed the request that
// failed: an entry is removed when its request settles, before any caller is told the outcome.
const inflightRequests = new Map<string, Promise<AxiosResponse>>();

function getDedupeKey(config: InternalAxiosRequestConfig): string | null {
  if (config.method?.toUpperCase() !== 'GET') return null;
  const url = `${config.baseURL || ''}${config.url || ''}`;
  const params = config.params ? JSON.stringify(config.params) : '';
  return `GET:${url}?${params}`;
}

/** The network adapter, sending identical GETs that are in flight together only once. */
function shareInflightGets(send: AxiosAdapter): AxiosAdapter {
  return (config: RetryConfig) => {
    // Cancelling a shared request would cancel it for everyone waiting on it, so one that can be
    // cancelled goes alone.
    if (!config._dedupeKey || config.signal || config.cancelToken) return send(config);

    // The same address is not enough. Sent with another token, response type or timeout, a GET can get
    // a different answer, so only requests sent the same way share one.
    const key = `${config._dedupeKey} ${JSON.stringify([config.headers, config.responseType, config.timeout])}`;
    let request = inflightRequests.get(key);
    if (!request) {
      request = send(config).finally(() => inflightRequests.delete(key));
      inflightRequests.set(key, request);
    }

    // Interceptors read and mark the config they find on a response or error (the token it carried, the
    // retries spent), so each caller gets the outcome bound to its own config, and its own copy of the
    // data to parse.
    return request.then(
      (response) => ({ ...response, config }),
      (error: unknown) => {
        if (!axios.isAxiosError(error)) throw error;
        const response = error.response && { ...error.response, config };
        throw AxiosError.from(error, error.code, config, error.request, response);
      },
    );
  };
}

// ─── Axios instance ─────────────────────────────────────────────────────
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/api/v1`
    : '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  adapter: shareInflightGets(axios.getAdapter(axios.defaults.adapter)),
});

// ─── Request interceptor: attach JWT + dedup key ─────────────────────────
api.interceptors.request.use(
  (config: RetryConfig) => {
    // A Login-as-Partner session that has run out hands the tab back to the staff member where they
    // started, instead of quietly sending the next request from a partner page with the staff token.
    const stored = getStoredImpersonation();
    if (stored && !getImpersonation()) {
      clearImpersonation();
      window.location.assign(stored.returnTo || '/partners');
    }

    // Only supply the staff token when the caller has not named one. The customer portal passes its
    // own subscriber token per request, and overwriting it sent a staff token to /portal/* — which
    // answered with an empty account rather than an error, so a signed-in admin saw a customer page
    // full of zeroes and believed it.
    // During Login as Partner this tab sends the partner-scoped token (sessionStorage); the staff
    // token in localStorage is left alone for every other tab.
    // The token is read again on every send, retries and replays included, so a request never goes
    // out again with a token that has since been renewed or signed out.
    if (config.headers) {
      const callerNamedToken = !!config.headers.Authorization && config._sentToken === undefined;
      if (!callerNamedToken) {
        const partnerToken = getImpersonation()?.token;
        const token = partnerToken ?? localStorage.getItem('isp_token');
        if (token) config.headers.Authorization = `Bearer ${token}`;
        else delete config.headers.Authorization;
        config._sentToken = token;
        config._asPartner = !!partnerToken;
      }
    }

    // Attach dedup key for GET requests: the adapter sends identical GETs in flight together once.
    const key = getDedupeKey(config);
    if (key) {
      config._dedupeKey = key;
    }

    // Kept from the first send, so a retry still knows which page asked.
    if (config._page === undefined) config._page = window.location.pathname;

    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ─── Staff session on a 401 ─────────────────────────────────────────────
// The staff session lives in localStorage and every tab shares it, so signing out here signs out every
// tab. A 401 used to do that on the spot. Now a request that carried a token which has since been
// replaced is simply sent again with the current one, an expired token is renewed with the refresh
// token, and only a refused renewal signs the user out.

type Renewal = 'renewed' | 'refused' | 'unavailable';

/**
 * Sign-in endpoints answer 401 for a wrong password or code. That is the form's answer, not a verdict on
 * the session the other tabs are using.
 */
const SIGN_IN_PATH =
  /^\/auth\/(?:login|verify-2fa|2fa\/validate|refresh|forgot-password|reset-password|sso\/ldap\/login)(?:[/?]|$)/;

/** True when a staff token is stored and it is not the one a request carried. */
function isReplacedToken(sent: string | null): boolean {
  const stored = localStorage.getItem('isp_token');
  return !!stored && stored !== sent;
}

let renewal: Promise<Renewal> | null = null;

/**
 * Trade the stored refresh token for a new pair — once, however many requests failed together. The API
 * rotates the refresh token on every use, so two tabs must not spend it at the same time: a Web Lock
 * queues them, and the tab that goes second finds the new token already stored.
 */
function renewSession(failedToken: string | null): Promise<Renewal> {
  if (!renewal) {
    renewal = renewUnderLock(failedToken).finally(() => {
      renewal = null;
    });
  }
  return renewal;
}

async function renewUnderLock(failedToken: string | null): Promise<Renewal> {
  // Web Locks need a secure context. Without them a tab still renews only once, and a refresh token
  // another tab spent a moment earlier is caught by re-reading storage after the API refuses it. Two tabs
  // spending it at the same instant are not: the API honours a refresh token once, so the second is refused.
  if (!navigator.locks) return renew(failedToken);
  return navigator.locks.request('isp-session-renewal', () => renew(failedToken));
}

async function renew(failedToken: string | null): Promise<Renewal> {
  // Renewed by another tab, or signed in again, while this one waited its turn.
  if (isReplacedToken(failedToken)) return 'renewed';
  const refreshToken = localStorage.getItem('isp_refresh_token');
  if (!refreshToken) return 'refused';

  try {
    const { data: resp } = await publicApi.post<{ data?: { accessToken?: string; refreshToken?: string } }>(
      '/auth/refresh',
      { refreshToken },
      { timeout: 15_000 },
    );
    const tokens = resp?.data;
    if (!tokens?.accessToken || !tokens.refreshToken) return 'refused';
    // Signed out, or in as someone else, in another tab while this was on the wire: that stands. The pair
    // just issued belongs to no session, so revoke it rather than leave its refresh token valid for a week.
    if (localStorage.getItem('isp_refresh_token') !== refreshToken) {
      revokeRefreshToken(tokens.refreshToken, tokens.accessToken);
      return isReplacedToken(failedToken) ? 'renewed' : 'refused';
    }
    localStorage.setItem('isp_token', tokens.accessToken);
    localStorage.setItem('isp_refresh_token', tokens.refreshToken);
    useAuthStore.setState({ token: tokens.accessToken, refreshToken: tokens.refreshToken });
    return 'renewed';
  } catch (err) {
    if (isReplacedToken(failedToken)) return 'renewed';
    // A refusal ends the session. Being offline, throttled or mid-restart says nothing about it.
    const status = (err as AxiosError).response?.status;
    return status === 400 || status === 401 || status === 403 ? 'refused' : 'unavailable';
  }
}

async function recoverStaffSession(error: AxiosError<ApiError>, config: RetryConfig): Promise<AxiosResponse> {
  const sent = config._sentToken ?? null;

  // It carried a token that has since been replaced — signed in again, or renewed, in another tab while
  // the request was on the wire. The 401 is about that old token: send it once more with the current one.
  if (isReplacedToken(sent) && !config._staleTokenRetried) {
    config._staleTokenRetried = true;
    return api(config);
  }

  // One renewal per request. A 401 on a freshly issued token is not an expired session (a wrong 2FA
  // code, say): it goes back to the caller and signs no one out.
  if (config._sessionRenewed) return Promise.reject(error);
  config._sessionRenewed = true;

  const outcome = await renewSession(sent);
  if (outcome === 'renewed') return api(config);
  // Could not renew right now: fail this request but keep the session, and let the next request try.
  if (outcome === 'unavailable') return Promise.reject(error);
  // Refused. A session signed in meanwhile in another tab is still not this request's to end.
  if (isReplacedToken(sent)) return Promise.reject(error);

  // Tell the login page why, but only when the API refused this tab's own refresh token. With none left, the
  // session was ended elsewhere (a sign-out, or the idle timer), which recorded its own reason.
  if (localStorage.getItem('isp_refresh_token')) recordSignOut('expired');
  localStorage.removeItem('isp_token');
  localStorage.removeItem('isp_refresh_token');
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
  return Promise.reject(error);
}

// ─── Response interceptor: retry + error handling ────────────────────────
// The dedup entry is not cleared here: the adapter removed it when the request settled, before this runs.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const config = error.config as RetryConfig | undefined;

    // ── 401 Unauthorized ──
    if (error.response?.status === 401) {
      const url = error.config?.url ?? '';

      // Customer-portal requests must never be answered with the STAFF login page. Two different
      // things arrive here as 401: a customer mistyping their code — which has to stay on the page
      // so they can try again — and a session that has genuinely expired, which belongs at the
      // customer login. Neither is a reason to clear a staff token that may not even exist.
      if (url.startsWith('/portal')) {
        const isSignIn = url.startsWith('/portal/login') || url.startsWith('/portal/otp');
        if (!isSignIn) {
          localStorage.removeItem('isp-portal-auth');
          if (!window.location.pathname.startsWith('/portal/login')) {
            window.location.href = '/portal/login';
          }
        }
        return Promise.reject(error);
      }

      // A Login-as-Partner session that ended — End session in another tab, the partner suspended,
      // the 30 minutes up — returns the staff member to their own session. It must never sign them
      // out of it.
      const impersonation = getStoredImpersonation();
      if (impersonation) {
        clearImpersonation();
        window.location.href = impersonation.returnTo || '/partners';
        return Promise.reject(error);
      }

      // Not about the staff session at all: a token the caller brought itself, a request sent as a
      // partner whose session this tab has already handed back, or a wrong password on a sign-in form.
      if (!config || config._sentToken === undefined || config._asPartner || SIGN_IN_PATH.test(url)) {
        return Promise.reject(error);
      }
      return recoverStaffSession(error, config);
    }

    // ── 403 Forbidden ──
    // Only surface a toast for user-initiated mutations. Background GETs (widgets, polling,
    // shell fetches) can legitimately 403 for permission-scoped roles — those fail silently
    // (the section just shows no data) instead of spamming "Access denied" toasts.
    if (error.response?.status === 403) {
      const method = (error.config?.method ?? 'get').toLowerCase();
      if (method !== 'get') toast.error('Access denied: insufficient permissions');
      return Promise.reject(error);
    }

    // ── Retry logic for transient failures ──
    if (config) {
      const retryCount = config._retryCount ?? 0;
      const isRetryable =
        !error.response || RETRYABLE_STATUS_CODES.has(error.response.status);
      const isIdempotent =
        config.method?.toUpperCase() === 'GET' ||
        config.method?.toUpperCase() === 'HEAD' ||
        config.method?.toUpperCase() === 'OPTIONS';

      // Nobody is waiting for the answer to a page the user has already left, so its read is dropped
      // quietly instead of retried. Those retries used to keep hitting the API after navigation —
      // nginx logged them with the next page as referer — and their toasts landed on the next page.
      const leftPage = () => config._page !== undefined && config._page !== window.location.pathname;

      if (isRetryable && isIdempotent && retryCount < MAX_RETRIES) {
        if (leftPage()) return Promise.reject(error);
        config._retryCount = retryCount + 1;

        // Exponential backoff: 1s, 2s
        const delay = RETRY_DELAY_MS * Math.pow(2, retryCount);
        await new Promise((resolve) => setTimeout(resolve, delay));

        if (leftPage()) return Promise.reject(error);
        return api(config);
      }
    }

    // ── Network error (no response at all) ──
    // A request may opt out of the global toast via `{ silent: true }` — used for best-effort live
    // reads (e.g. slow OLT WAN/Wi-Fi/service-path SNMP) that render their own graceful empty state.
    if (!error.response && error.code !== 'ERR_CANCELED' && !(error.config as any)?.silent) {
      toast.error('Network error — please check your connection');
    }

    // ── Server error toast (5xx, excluding retry exhaustion) ──
    if (error.response && error.response.status >= 500) {
      const msg =
        error.response.data?.message || 'Server error — please try again later';
      toast.error(msg);
    }

    return Promise.reject(error);
  },
);

// ─── Public axios instance (NO JWT) ──────────────────────────────────────
// Defined in lib/public-api; re-exported so `import { publicApi } from '@/lib/api'` keeps working.
export { publicApi };

export default api;
